import { useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, ChevronDown, ChevronUp, Copy, CreditCard, ExternalLink, FileText, Layers, Link as LinkIcon, Loader2, MoreHorizontal, Pencil, Plus, ReceiptText, Repeat, RotateCcw, RefreshCw, Send, Trash2 } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Checkbox } from '~/components/ui/checkbox';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { useFamilyBillingQuery } from './useFamilyBillingQuery';
import { useFamilyStripePaymentMethodsQuery } from './useFamilyStripePaymentMethodsQuery';
import { useSetDefaultPaymentMethod } from './useSetDefaultPaymentMethod';
import { useSetSubscriptionPaymentMethod } from './useSetSubscriptionPaymentMethod';
import {
  useCancelSubscriptionAtPeriodEnd,
  useCancelSubscriptionImmediately,
  useChangeSubscriptionNextCycleAmount,
  useCreatePaymentMethodUpdateLink,
  useConvertSubscriptionToInvoices,
  useMoveSubscriptionBillingDate,
  useReconcileSubscriptionDates,
  useResumeSubscription,
} from './useSubscriptionAdminActions';
import { useCheckSubscriptionDrift } from './useCheckSubscriptionDrift';
import { useSyncPaymentMethodFromStripe } from './useSyncPaymentMethodFromStripe';
import { useSyncFamilyBilling } from './useSyncFamilyBilling';
import type { SyncFamilyBillingReport } from './useSyncFamilyBilling';
import {
  useDeleteInvoice,
  useMarkInvoicePaidOutOfBand,
  useRetryInvoicePayment,
  useSafeVoidInvoice,
} from './useBillingInvoiceActions';
import { InvoiceLineItemEditor } from './InvoiceLineItemEditor';
import { usePostInvoices } from './usePostInvoices';
import { InvoiceNotice } from './InvoiceNotice';
import { useUpdateInvoice } from './useUpdateInvoice';
import InvoiceSetWizard from './InvoiceSetWizard';
import { ACCESS_LABELS, isNewStyle } from '../Enrollments/enrollmentPhases';
import type { InvoiceSetStudent, InvoiceSetWizardMode } from './InvoiceSetWizard';
import type {
  FamilyBillingInvoice,
  FamilyBillingSubscription,
  FamilyBillingWarning,
} from './useFamilyBillingQuery';

interface BillingTabProps {
  schoolId: string;
  familySlug: string | undefined;
  schoolYearId: string | undefined;
  selectedYear: { name: string } | null | undefined;
  isActive: boolean;
  students?: readonly InvoiceSetStudent[];
  /** The school enrolls with the checklist (`School.checklistEnrollmentOn`). */
  checklistEnrollmentOn?: boolean;
  /** The family year on the page, for its access. */
  familyYear?: { access: string; accessSource: string } | null;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatCurrency(cents: number) {
  return currencyFormatter.format(cents / 100);
}

function formatDate(value: unknown) {
  if (typeof value !== 'string' || !value) return 'N/A';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(value: unknown) {
  if (typeof value !== 'string' || !value) return 'N/A';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Rows are ordered by when the family owes them; an invoice with no due date
// sorts last rather than jumping to the top of the list.
function dueTime(value: unknown) {
  if (typeof value !== 'string' || !value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(`${value}T00:00:00`).getTime();
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function titleize(value: string | null | undefined) {
  if (!value) return 'N/A';
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusVariant(displayStatus: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (displayStatus === 'Paid') return 'default';
  if (displayStatus === 'Payment Failed') return 'destructive';
  if (displayStatus === 'Payment Processing' || displayStatus === 'Scheduled') return 'secondary';
  return 'outline';
}

function isProcessingInvoice(invoice: FamilyBillingInvoice) {
  return invoice.status === 'PAYMENT_PROCESSING' ||
    invoice.checkoutPaymentStatus === 'checkout_processing' ||
    invoice.autopayStatus === 'autopay_processing';
}

function voidDisabledReason(invoice: FamilyBillingInvoice) {
  if (isProcessingInvoice(invoice)) return 'Payment is processing; wait for Stripe settlement.';
  if (invoice.status === 'PAID') return 'Paid invoices need refund or credit handling.';
  if (invoice.status === 'VOID') return 'Invoice is already voided.';
  if (invoice.status === 'DRAFT') return 'Delete draft invoices instead.';
  if (!['UNPAID', 'SCHEDULED', 'PAYMENT_FAILED'].includes(invoice.status)) return 'Invoice cannot be voided in this state.';
  return null;
}

function retryDisabledReason(invoice: FamilyBillingInvoice) {
  if (isProcessingInvoice(invoice)) return 'Payment is processing; wait for Stripe settlement.';
  if (invoice.status === 'PAID') return 'Invoice is already paid.';
  if (invoice.status === 'VOID') return 'Voided invoices cannot be retried.';
  if (invoice.checkoutPaymentStatus !== 'checkout_failed') return 'Only failed checkouts can be retried.';
  return null;
}

function markPaidDisabledReason(invoice: FamilyBillingInvoice) {
  if (isProcessingInvoice(invoice)) return 'Payment is processing; wait for Stripe settlement.';
  if (invoice.status === 'PAID') return 'Invoice is already paid.';
  if (invoice.status === 'VOID') return 'Voided invoices cannot be marked paid.';
  if (invoice.status === 'SCHEDULED' && invoice.subscriptionManaged) {
    return 'Scheduled subscription invoice — move billing date or cancel subscription first.';
  }
  if (invoice.stripePaymentIntentId) return 'A Stripe payment intent already exists; reconcile it first.';
  // A draft is not in Stripe, so cash or a check for it is recorded here.
  if (!['DRAFT', 'UNPAID', 'SCHEDULED', 'PAYMENT_FAILED'].includes(invoice.status)) return 'Invoice cannot be marked paid in this state.';
  return null;
}

// A draft exists only in SolaStack, so its due date and memo are edited in
// place; anything in Stripe is changed there. The backend says which rows are
// editable.
function editDisabledReason(invoice: FamilyBillingInvoice) {
  return invoice.editable ? null : 'Only draft invoices can be edited.';
}

function invoiceLabel(invoice: FamilyBillingInvoice) {
  return invoice.memo || titleize(invoice.key);
}

function pluralInvoices(count: number) {
  return `${count} ${count === 1 ? 'invoice' : 'invoices'}`;
}

function collectionLabel(invoice: FamilyBillingInvoice) {
  // A DRAFT row has no Stripe object yet, so its authorization flag says nothing
  // about how it will be collected.
  if (invoice.status === 'DRAFT') return 'Draft';
  if (invoice.stripeCollectionMethod === 'charge_automatically' || invoice.automaticPaymentAuthorized) {
    return 'Auto-pay';
  }
  if (invoice.stripeCollectionMethod === 'send_invoice') return 'Manual payment';
  return 'Not in Stripe';
}

interface AutoPayBilling {
  paymentMethod: { autopayEnabled: boolean; usableForAutomaticBilling: boolean };
  familyAutoPayAuthorized: boolean;
  autoPayWaived: boolean;
  autoPayWaivedReason: string | null;
}

// Decision 3: PostInvoices charges a family automatically only when it authorized
// auto-pay (a signed packet or a grandfathered subscription) and has a saved
// method. `autopayEnabled` here is that authorization, not the bare flag. A
// family whose checklist authorization doesn't cover this year yet is told so.
function autoPayUnavailableReason(billing: AutoPayBilling) {
  const { paymentMethod } = billing;
  if (!paymentMethod.autopayEnabled) {
    return billing.familyAutoPayAuthorized
      ? "This family has authorized auto-pay, but it doesn't cover this year yet, so its invoices are sent for them to pay."
      : "This family hasn't authorized automatic payments, so its invoices are sent for them to pay.";
  }
  if (!paymentMethod.usableForAutomaticBilling) {
    return 'This family authorized automatic payments but has no saved payment method to charge.';
  }
  return null;
}

// The badge says what posting does for the year, the same authorization the
// post dialog reads; the note says where the checklist's auto-pay step reads
// otherwise. A year whose step was waived and isn't charged reads Waived. With
// no saved method to charge, the note is the post dialog's own reason, waived
// or not.
function autoPayStatus(billing: AutoPayBilling): { word: string; on: boolean; note: string | null } {
  const reason = billing.autoPayWaivedReason;
  if (billing.paymentMethod.autopayEnabled) {
    if (!billing.paymentMethod.usableForAutomaticBilling) {
      return { word: 'Enabled', on: true, note: autoPayUnavailableReason(billing) };
    }
    const note = billing.autoPayWaived
      ? `The auto-pay step was waived${reason ? ` (${reason})` : ''}, but the family's auto-pay covers this year, so its payments are charged automatically.`
      : null;
    return { word: 'Enabled', on: true, note };
  }
  if (billing.autoPayWaived) return { word: 'Waived', on: false, note: reason };
  return { word: 'Disabled', on: false, note: billing.familyAutoPayAuthorized ? autoPayUnavailableReason(billing) : null };
}

function StripeLink({ href }: { href: string | null | undefined }) {
  if (!href) return <span className="text-muted-foreground">None</span>;

  return (
    <Button asChild variant="ghost" size="sm" className="h-8 px-2">
      <a href={href} target="_blank" rel="noreferrer">
        <ExternalLink className="h-4 w-4" />
        <span className="sr-only">Open Stripe</span>
      </a>
    </Button>
  );
}

function InvoiceStripeLinks({ invoice }: { invoice: FamilyBillingInvoice }) {
  const links = [
    {
      href: invoice.stripeDashboardUrl,
      label: 'Open Stripe',
      icon: <ExternalLink className="h-4 w-4" />,
    },
    {
      href: invoice.stripeHostedInvoiceUrl,
      label: 'Open hosted invoice',
      icon: <ReceiptText className="h-4 w-4" />,
    },
    {
      href: invoice.stripeInvoicePdfUrl,
      label: 'Open invoice PDF',
      icon: <FileText className="h-4 w-4" />,
    },
  ].filter((link) => link.href);

  if (links.length === 0) return <span className="text-muted-foreground">None</span>;

  return (
    <div className="flex items-center gap-1">
      {links.map((link) => (
        <Button key={link.label} asChild variant="ghost" size="sm" className="h-8 px-2">
          <a href={link.href || '#'} target="_blank" rel="noreferrer">
            {link.icon}
            <span className="sr-only">{link.label}</span>
          </a>
        </Button>
      ))}
    </div>
  );
}

function WarningList({ warnings }: { warnings: readonly FamilyBillingWarning[] }) {
  if (warnings.length === 0) return null;

  return (
    <div className="space-y-2">
      {warnings.map((warning) => (
        <div
          key={warning.code}
          className="flex gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
          <div>
            <div className="font-medium">{titleize(warning.code)}</div>
            <div>{warning.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function mutationError(payload: { userErrors?: readonly { message: string }[] | null } | null | undefined) {
  return payload?.userErrors?.[0]?.message || null;
}

interface ConvertInvoiceRow {
  invoiceId: string;
  dueDate: string | null;
  amountCents: number;
  // Ruling 30: the card fee the auto-pay method attracts, as its own column, so
  // the admin sees what a card family will actually be charged. 0 for a bank.
  feeCents: number;
  memo: string | null;
  action: string;
}

function hasCardFees(rows: readonly { feeCents: number }[]) {
  return rows.some((row) => row.feeCents > 0);
}

interface ConvertReport {
  dryRun: boolean;
  stripeStatus: string | null;
  periodEndOn: string | null;
  cancelAtOn: string | null;
  pushedCount: number;
  reconciledCount: number;
  warnings: readonly string[];
  invoices: readonly ConvertInvoiceRow[];
}

function ConvertWarnings({ warnings }: { warnings: readonly string[] }) {
  if (warnings.length === 0) return null;

  return (
    <div className="space-y-1 text-amber-700">
      {warnings.map((warning) => (
        <div key={warning} className="flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
          <span>{warning}</span>
        </div>
      ))}
    </div>
  );
}

function convertRowStatus(row: ConvertInvoiceRow) {
  if (row.action === 'already_pushed') return 'already in Stripe';
  if (row.action === 'pushed') return `pushed to Stripe, charges on ${formatDate(row.dueDate)}`;
  return `will charge automatically on ${formatDate(row.dueDate)}`;
}

// C1 cancels the converted subscription on an explicit date, so Stripe reports
// cancel_at_period_end as false. A cancel_at at or before the period end is the
// real signal; the deferred school-year-end cancel_at every subscription carries
// is later than the period end and must not read as "cancelling".
function hasScheduledCancellation(subscription: FamilyBillingSubscription) {
  if (subscription.cancelAtPeriodEnd) return true;
  if (!subscription.cancelAt || !subscription.currentPeriodEndAt) return false;
  const cancelAt = new Date(subscription.cancelAt).getTime();
  const periodEnd = new Date(subscription.currentPeriodEndAt).getTime();
  if (!Number.isFinite(cancelAt) || !Number.isFinite(periodEnd)) return false;
  return cancelAt <= periodEnd;
}

function formatRelativeFromNow(value: string | null | undefined) {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return null;
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function SubscriptionDriftSection({
  subscription,
  onActionCompleted,
}: {
  subscription: FamilyBillingSubscription;
  onActionCompleted: () => void;
}) {
  const [actionError, setActionError] = useState<string | null>(null);
  const [checkDrift, { loading: checkLoading }] = useCheckSubscriptionDrift();
  const [reconcileSubscriptionDates, { loading: reconcileLoading }] = useReconcileSubscriptionDates();

  const handleCheck = async () => {
    setActionError(null);
    const result = await checkDrift({
      variables: { input: { subscriptionId: subscription.subscriptionId } },
    });
    const message = mutationError(result.data?.checkSubscriptionDrift);
    if (message) {
      setActionError(message);
      return;
    }

    onActionCompleted();
  };

  const handleReconcile = async () => {
    setActionError(null);
    const result = await reconcileSubscriptionDates({
      variables: { input: { subscriptionId: subscription.subscriptionId } },
    });
    const message = mutationError(result.data?.reconcileSubscriptionDates);
    if (message) {
      setActionError(message);
      return;
    }

    onActionCompleted();
  };

  const lastChecked = subscription.lastDriftCheckAt
    ? `Last checked ${formatRelativeFromNow(subscription.lastDriftCheckAt) || formatDateTime(subscription.lastDriftCheckAt)}`
    : 'Never checked';

  return (
    <div className="border-t pt-3 space-y-2">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-muted-foreground">
          Stripe date drift: {lastChecked}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={checkLoading}
          onClick={() => { void handleCheck(); }}
        >
          {checkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Check for drift
        </Button>
      </div>

      {subscription.driftDetected && subscription.driftDetails && (
        <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
            <div>
              <div className="font-medium">Subscription Date Drift</div>
              <div>{subscription.driftDetails.message}</div>
              <div className="mt-1 text-xs text-amber-800">
                Stripe: {formatDate(subscription.driftDetails.stripeDate)} - Local: {formatDate(subscription.driftDetails.localDate)}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-amber-300 bg-white md:w-auto"
            disabled={reconcileLoading}
            onClick={() => { void handleReconcile(); }}
          >
            {reconcileLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Reconcile from Stripe
          </Button>
        </div>
      )}

      {!subscription.driftDetected && subscription.lastDriftCheckAt && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          <CheckCircle2 className="h-4 w-4" />
          No drift detected. Local subscription dates match Stripe.
        </div>
      )}

      {actionError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}
    </div>
  );
}

function InvoiceRowsTable({
  invoices,
  onActionCompleted,
  selectedIds,
  onToggleSelected,
}: {
  invoices: readonly FamilyBillingInvoice[];
  onActionCompleted: () => void;
  // When the caller can post rows it passes the selection; the table then adds
  // a checkbox to every row the backend marks postable. Without it (a live
  // subscription's rows) there is no selection column at all.
  selectedIds?: ReadonlySet<string>;
  onToggleSelected?: (invoiceId: string, selected: boolean) => void;
}) {
  const selectable = !!onToggleSelected;
  const columnCount = selectable ? 10 : 9;
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState<FamilyBillingInvoice | null>(null);
  const [invoiceToEdit, setInvoiceToEdit] = useState<FamilyBillingInvoice | null>(null);
  const [editDueDate, setEditDueDate] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [invoiceToVoid, setInvoiceToVoid] = useState<FamilyBillingInvoice | null>(null);
  const [invoiceToRetry, setInvoiceToRetry] = useState<FamilyBillingInvoice | null>(null);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState<FamilyBillingInvoice | null>(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);
  const [paidAmount, setPaidAmount] = useState('0.00');
  const [paidMethod, setPaidMethod] = useState('CHECK');
  const [paidNote, setPaidNote] = useState('');

  const [deleteInvoice, { loading: deletingInvoice }] = useDeleteInvoice();
  const [voidInvoice, { loading: voidingInvoice }] = useSafeVoidInvoice();
  const [retryInvoice, { loading: retryingInvoice }] = useRetryInvoicePayment();
  const [markInvoicePaid, { loading: markingPaid }] = useMarkInvoicePaidOutOfBand();
  const [updateInvoice, { loading: updatingInvoice }] = useUpdateInvoice();

  const openEditDialog = (invoice: FamilyBillingInvoice) => {
    setActionError(null);
    setActionNotice(null);
    setInvoiceToEdit(invoice);
    setEditDueDate(typeof invoice.dueDate === 'string' ? invoice.dueDate : '');
    setEditMemo(invoice.memo ?? '');
  };

  const openMarkPaidDialog = (invoice: FamilyBillingInvoice) => {
    setActionError(null);
    setActionNotice(null);
    setInvoiceToMarkPaid(invoice);
    setPaidAmount((invoice.amountCents / 100).toFixed(2));
    setPaidMethod('CHECK');
    setPaidNote('');
  };

  const runInvoiceAction = async (
    mutate: any,
    input: Record<string, unknown>,
    payloadKey: string,
    close: () => void,
    notice?: string
  ) => {
    setActionError(null);
    setActionNotice(null);
    const result = await mutate({ variables: { input } });
    const message = mutationError(result.data?.[payloadKey]);
    if (message) {
      setActionError(message);
      return;
    }

    close();
    if (notice) setActionNotice(notice);
    onActionCompleted();
  };

  const handleEditInvoice = async () => {
    if (!invoiceToEdit) return;

    if (!editDueDate) {
      setActionError('Due date is required.');
      return;
    }

    await runInvoiceAction(
      updateInvoice,
      {
        invoiceId: invoiceToEdit.invoiceId,
        dueDate: editDueDate,
        memo: editMemo.trim() || null,
      },
      'updateInvoice',
      () => setInvoiceToEdit(null),
      'Invoice updated.'
    );
  };

  const handleMarkPaid = async () => {
    if (!invoiceToMarkPaid) return;

    const amountCents = Math.round(parseFloat(paidAmount || '0') * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setActionError('Amount must be greater than $0.');
      return;
    }

    await runInvoiceAction(
      markInvoicePaid,
      {
        invoiceId: invoiceToMarkPaid.invoiceId,
        amountCents,
        paymentMethod: paidMethod,
        note: paidNote || null,
      },
      'markInvoicePaidOutOfBand',
      () => setInvoiceToMarkPaid(null)
    );
  };

  return (
    <div className="space-y-3">
      {actionError && !invoiceToEdit && !invoiceToMarkPaid && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}
      {actionNotice && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {actionNotice}
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-8">
                  <span className="sr-only">Select</span>
                </TableHead>
              )}
              <TableHead>Invoice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Collection</TableHead>
              <TableHead>Posted</TableHead>
              <TableHead>Notice</TableHead>
              <TableHead>Stripe</TableHead>
              <TableHead className="w-24">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.flatMap((invoice) => {
              const deleteReason = invoice.status === 'DRAFT' ? null : 'Only draft invoices can be deleted.';
              const invoiceEditReason = editDisabledReason(invoice);
              const invoiceVoidReason = voidDisabledReason(invoice);
              const invoiceRetryReason = retryDisabledReason(invoice);
              const invoiceMarkPaidReason = markPaidDisabledReason(invoice);
              const disabledReasons = Array.from(
                new Set(
                  [invoiceEditReason, invoiceVoidReason, invoiceRetryReason, invoiceMarkPaidReason].filter(
                    (reason): reason is string => !!reason
                  )
                )
              );
              const isDraft = invoice.status === 'DRAFT';
              const expanded = expandedInvoiceId === invoice.invoiceId;
              const hasLines = isDraft || invoice.lineItems.length > 0;
              const label = invoiceLabel(invoice);

              return [
                <TableRow key={invoice.invoiceId}>
                  {selectable && (
                    // The backend decides what can be posted. A row that cannot
                    // gets no checkbox; the reason rides on the cell.
                    <TableCell title={invoice.postable ? undefined : invoice.postDisabledReason ?? undefined}>
                      {invoice.postable && (
                        <Checkbox
                          aria-label={`Select ${label}`}
                          checked={selectedIds?.has(invoice.invoiceId) ?? false}
                          onCheckedChange={(checked) => onToggleSelected?.(invoice.invoiceId, checked === true)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground">{invoice.key || invoice.invoiceId}</div>
                    {invoice.warningCodes.length > 0 && (
                      <div className="mt-1 text-xs text-amber-700">
                        {invoice.warningCodes.map(titleize).join(', ')}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(invoice.displayStatus)}>
                      {invoice.displayStatus}
                    </Badge>
                    {invoice.subscriptionManaged && (
                      <Badge variant="outline" className="ml-2">Autopay</Badge>
                    )}
                    {/* A void row never reached Stripe, so posting it brings it
                        back as a live invoice rather than creating a new one. */}
                    {invoice.status === 'VOID' && invoice.postable && (
                      <div className="mt-1 text-xs text-muted-foreground">Void — posting restores it</div>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(invoice.amountCents)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {collectionLabel(invoice)}
                  </TableCell>
                  <TableCell>{formatDateTime(invoice.postedAt)}</TableCell>
                  <TableCell><InvoiceNotice invoice={invoice} onCompleted={onActionCompleted} /></TableCell>
                  <TableCell>
                    <InvoiceStripeLinks invoice={invoice} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Ruling 27: a draft's lines are adjusted in place before
                          it is sent, so the editor is a row action like any
                          other rather than something only the invoice row
                          expander offered. */}
                      {hasLines && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          aria-label={isDraft ? 'Edit line items' : 'Show line items'}
                          onClick={() => setExpandedInvoiceId(expanded ? null : invoice.invoiceId)}
                        >
                          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 px-2">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Invoice actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isProcessingInvoice(invoice) && (
                            <DropdownMenuItem disabled>
                              Payment is processing; wait for Stripe settlement.
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            disabled={!!invoiceEditReason}
                            onClick={() => openEditDialog(invoice)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit due date & memo
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!!deleteReason}
                            onClick={() => {
                              setActionError(null);
                              setActionNotice(null);
                              setInvoiceToDelete(invoice);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete draft
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!!invoiceVoidReason}
                            onClick={() => {
                              setActionError(null);
                              setActionNotice(null);
                              setInvoiceToVoid(invoice);
                            }}
                          >
                            <Ban className="h-4 w-4" />
                            {invoice.stripeCheckoutSessionId ? 'Cancel payment request' : 'Void unpaid invoice'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!!invoiceRetryReason}
                            onClick={() => {
                              setActionError(null);
                              setActionNotice(null);
                              setInvoiceToRetry(invoice);
                            }}
                          >
                            <RotateCcw className="h-4 w-4" />
                            Retry payment
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!!invoiceMarkPaidReason}
                            onClick={() => openMarkPaidDialog(invoice)}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Mark paid out of band
                          </DropdownMenuItem>
                          {disabledReasons.length > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              {disabledReasons.map((reason) => (
                                <DropdownMenuItem key={reason} disabled className="max-w-64 whitespace-normal text-xs">
                                  {reason}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                          {invoice.stripeHostedInvoiceUrl && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem asChild>
                                <a href={invoice.stripeHostedInvoiceUrl} target="_blank" rel="noreferrer">
                                  <ExternalLink className="h-4 w-4" />
                                  Hosted invoice
                                </a>
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>,
                expanded ? (
                  <TableRow key={`${invoice.invoiceId}-lines`}>
                    <TableCell colSpan={columnCount} className="bg-muted/30">
                      <InvoiceLineItemEditor
                        invoiceId={invoice.invoiceId}
                        totalCents={invoice.amountCents}
                        editable={isDraft}
                        lineItems={invoice.lineItems.map((line) => ({
                          id: line.lineItemId,
                          description: line.description,
                          amountCents: line.amountCents,
                          lineType: line.lineType,
                        }))}
                        onChanged={onActionCompleted}
                      />
                    </TableCell>
                  </TableRow>
                ) : null,
              ];
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!invoiceToDelete} onOpenChange={(open) => { if (!open) setInvoiceToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the draft invoice row. Posted, paid, processing, and Stripe-backed invoices are not deleted here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (!invoiceToDelete) return;
                void runInvoiceAction(
                  deleteInvoice,
                  { invoiceId: invoiceToDelete.invoiceId },
                  'deleteInvoice',
                  () => setInvoiceToDelete(null)
                );
              }}
              disabled={deletingInvoice}
            >
              {deletingInvoice ? 'Deleting...' : 'Delete Draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!invoiceToVoid} onOpenChange={(open) => { if (!open) setInvoiceToVoid(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Any open Stripe Checkout Session or Stripe Invoice will be canceled before the local invoice is voided.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (!invoiceToVoid) return;
                void runInvoiceAction(
                  voidInvoice,
                  { invoiceId: invoiceToVoid.invoiceId },
                  'voidInvoice',
                  () => setInvoiceToVoid(null)
                );
              }}
              disabled={voidingInvoice}
              className="bg-destructive text-destructive-foreground"
            >
              {voidingInvoice ? 'Voiding...' : 'Void Invoice'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!invoiceToRetry} onOpenChange={(open) => { if (!open) setInvoiceToRetry(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retry payment?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the failed checkout state so the family can start a fresh payment attempt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (!invoiceToRetry) return;
                void runInvoiceAction(
                  retryInvoice,
                  { invoiceId: invoiceToRetry.invoiceId },
                  'retryInvoicePayment',
                  () => setInvoiceToRetry(null)
                );
              }}
              disabled={retryingInvoice}
            >
              {retryingInvoice ? 'Resetting...' : 'Retry Payment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!invoiceToEdit} onOpenChange={(open) => { if (!open) setInvoiceToEdit(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit draft invoice</DialogTitle>
            <DialogDescription>
              Change when {invoiceToEdit ? invoiceLabel(invoiceToEdit) : 'this draft'} is due and what it says.
              Line items are edited on the row.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-invoice-due-date">Due date</Label>
              <Input
                id="edit-invoice-due-date"
                type="date"
                value={editDueDate}
                onChange={(event) => setEditDueDate(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-invoice-memo">Memo</Label>
              <Input
                id="edit-invoice-memo"
                value={editMemo}
                onChange={(event) => setEditMemo(event.target.value)}
                placeholder="Shown on the invoice"
              />
            </div>
            {actionError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {actionError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceToEdit(null)} disabled={updatingInvoice}>
              Cancel
            </Button>
            <Button onClick={() => { void handleEditInvoice(); }} disabled={updatingInvoice}>
              {updatingInvoice ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invoiceToMarkPaid} onOpenChange={(open) => { if (!open) setInvoiceToMarkPaid(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Paid Out Of Band</DialogTitle>
            <DialogDescription>
              Record a check, cash, or other non-Stripe payment for this invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="oob-paid-amount">Amount</Label>
                <Input
                  id="oob-paid-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paidAmount}
                  onChange={(event) => setPaidAmount(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Payment method</Label>
                <Select value={paidMethod} onValueChange={setPaidMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECK">Check</SelectItem>
                    <SelectItem value="CASH">Cash</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="oob-paid-note">Note</Label>
              <Input
                id="oob-paid-note"
                value={paidNote}
                onChange={(event) => setPaidNote(event.target.value)}
                placeholder="Check #100"
              />
            </div>
            {actionError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {actionError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceToMarkPaid(null)}>Cancel</Button>
            <Button onClick={handleMarkPaid} disabled={markingPaid}>
              {markingPaid ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubscriptionCard({
  subscription,
  onActionCompleted,
}: {
  subscription: FamilyBillingSubscription;
  onActionCompleted: () => void;
}) {
  const [actionError, setActionError] = useState<string | null>(null);
  const [showCancelAtPeriodEnd, setShowCancelAtPeriodEnd] = useState(false);
  const [showCancelImmediately, setShowCancelImmediately] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [showChangeAmount, setShowChangeAmount] = useState(false);
  const [showMoveBillingDate, setShowMoveBillingDate] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [convertReport, setConvertReport] = useState<ConvertReport | null>(null);
  const [convertNotice, setConvertNotice] = useState<string | null>(null);
  const [convertWarnings, setConvertWarnings] = useState<readonly string[]>([]);
  const [billingDate, setBillingDate] = useState(subscription.startDate || '');

  const nextScheduledInvoice = subscription.invoices.find((invoice) => invoice.status === 'SCHEDULED');
  const [nextAmount, setNextAmount] = useState(
    nextScheduledInvoice ? (nextScheduledInvoice.amountCents / 100).toFixed(2) : '0.00'
  );

  const [cancelAtPeriodEnd, { loading: cancelAtPeriodEndLoading }] = useCancelSubscriptionAtPeriodEnd();
  const [cancelImmediately, { loading: cancelImmediatelyLoading }] = useCancelSubscriptionImmediately();
  const [resumeSubscription, { loading: resumeLoading }] = useResumeSubscription();
  const [changeNextAmount, { loading: changeAmountLoading }] = useChangeSubscriptionNextCycleAmount();
  const [moveBillingDate, { loading: moveBillingDateLoading }] = useMoveSubscriptionBillingDate();
  const [convertToInvoices, { loading: convertLoading }] = useConvertSubscriptionToInvoices();

  const isCanceled = subscription.status === 'subscription_canceled';
  const isPaused = subscription.status === 'subscription_paused';
  const isPastDue = subscription.status === 'subscription_past_due';
  const hasProcessingPayment = subscription.hasPaymentProcessingInvoice;
  const cancellationScheduled = hasScheduledCancellation(subscription);
  const canResume = !isCanceled && (subscription.cancelAtPeriodEnd || isPaused);
  const canConvert = !isCanceled && !cancellationScheduled;
  // A converted subscription's remaining installments are standalone Stripe
  // invoices; the subscription no longer bills them. Adjusting one from here
  // would attach a pending InvoiceItem to the subscription, charging the delta a
  // second time on the next cycle while the pushed invoice collects in full.
  const canChangeAmount = !isCanceled && !subscription.converted && !!nextScheduledInvoice;
  const canMoveBillingDate = subscription.locallyManaged && !isCanceled && !isPastDue && !hasProcessingPayment;
  const cancelImmediatelyDisabledReason = isCanceled
    ? 'Subscription is already canceled.'
    : hasProcessingPayment
      ? 'A child invoice payment is processing.'
      : null;

  const runAction = async (
    mutate: any,
    input: Record<string, unknown>,
    payloadKey: string,
    close: () => void
  ) => {
    setActionError(null);
    const result = await mutate({ variables: { input } });
    const message = mutationError(result.data?.[payloadKey]);
    if (message) {
      setActionError(message);
      return;
    }

    close();
    onActionCompleted();
  };

  // Two round trips on purpose: the first is a dry run whose report the admin
  // approves, the second applies it and carries that report's period end back so
  // a subscription that renewed in between is refused before anything is pushed.
  const handlePreviewConvert = async () => {
    setActionError(null);
    setConvertNotice(null);
    setConvertWarnings([]);
    setConvertReport(null);

    const result = await convertToInvoices({
      variables: { input: { subscriptionId: subscription.subscriptionId, confirm: false } },
    });
    const message = mutationError(result.data?.convertSubscriptionToInvoices);
    if (message) {
      setActionError(message);
      return;
    }

    const report = result.data?.convertSubscriptionToInvoices?.report;
    if (!report) {
      setActionError('The conversion preview did not return a report.');
      return;
    }

    setConvertReport(report);
    setShowConvert(true);
  };

  // The dialog is closed here rather than by AlertDialogAction's default, so it
  // stays open showing "Converting..." for however long the pushes take.
  const handleConvert = async () => {
    if (!convertReport) return;

    setActionError(null);
    setConvertWarnings([]);
    const result = await convertToInvoices({
      variables: {
        input: {
          subscriptionId: subscription.subscriptionId,
          confirm: true,
          expectedPeriodEndOn: convertReport.periodEndOn,
        },
      },
    });

    setShowConvert(false);
    setConvertReport(null);

    const message = mutationError(result.data?.convertSubscriptionToInvoices);
    if (message) {
      setActionError(message);
      return;
    }

    const report = result.data?.convertSubscriptionToInvoices?.report;
    if (report) {
      // A partial run leaves the subscription collecting, and pushes nothing at
      // all when the first invoice is rejected. Warnings carry that; a "converted
      // 0 installments" line would only contradict them.
      setConvertWarnings(report.warnings);
      const notices = [
        report.pushedCount > 0 ? `Converted ${report.pushedCount} installment(s) to invoices.` : null,
        report.reconciledCount > 0
          ? `Reconciled ${report.reconciledCount} already-billed month(s) from Stripe.`
          : null,
      ].filter(Boolean);
      setConvertNotice(notices.length > 0 ? notices.join(' ') : null);
    }
    onActionCompleted();
  };

  const handleChangeAmount = async () => {
    const amountCents = Math.round(parseFloat(nextAmount || '0') * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setActionError('Amount must be greater than $0.');
      return;
    }

    await runAction(
      changeNextAmount,
      { subscriptionId: subscription.subscriptionId, amountCents },
      'changeSubscriptionNextCycleAmount',
      () => setShowChangeAmount(false)
    );
  };

  const handleMoveBillingDate = async () => {
    if (!billingDate) {
      setActionError('Billing date is required.');
      return;
    }

    await runAction(
      moveBillingDate,
      { subscriptionId: subscription.subscriptionId, billingDate },
      'moveSubscriptionBillingDate',
      () => setShowMoveBillingDate(false)
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <Repeat className="h-4 w-4" />
            Subscription
          </span>
          <span className="flex items-center gap-1">
            <StripeLink href={subscription.stripeDashboardUrl} />
            {subscription.locallyManaged && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 px-2">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Subscription actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={isCanceled || cancellationScheduled}
                    onClick={() => {
                      setActionError(null);
                      setShowCancelAtPeriodEnd(true);
                    }}
                  >
                    <Ban className="h-4 w-4" />
                    Cancel at period end
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!!cancelImmediatelyDisabledReason}
                    onClick={() => {
                      setActionError(null);
                      setShowCancelImmediately(true);
                    }}
                  >
                    <Ban className="h-4 w-4" />
                    Cancel immediately
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!canResume}
                    onClick={() => {
                      setActionError(null);
                      setShowResume(true);
                    }}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Resume
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={!canChangeAmount}
                    onClick={() => {
                      setActionError(null);
                      setNextAmount(nextScheduledInvoice ? (nextScheduledInvoice.amountCents / 100).toFixed(2) : '0.00');
                      setShowChangeAmount(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                    Change next amount
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!canMoveBillingDate}
                    onClick={() => {
                      setActionError(null);
                      setBillingDate(subscription.startDate || '');
                      setShowMoveBillingDate(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                    Move billing date
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={!canConvert || convertLoading}
                    onClick={() => handlePreviewConvert()}
                  >
                    <ReceiptText className="h-4 w-4" />
                    Convert to invoices...
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {actionError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
            {actionError}
          </div>
        )}

        {convertNotice && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
            {convertNotice}
          </div>
        )}

        {convertWarnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <ConvertWarnings warnings={convertWarnings} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div>
            <div className="text-muted-foreground">Status</div>
            <Badge variant="outline">{titleize(subscription.status)}</Badge>
          </div>
          <div>
            <div className="text-muted-foreground">Start</div>
            <div className="font-medium">{formatDate(subscription.startDate)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Billing Day</div>
            <div className="font-medium">{subscription.billingDayOfMonth ?? 'N/A'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">{cancellationScheduled ? 'Cancels' : 'Scheduled'}</div>
            <div className="font-medium">
              {cancellationScheduled
                ? formatDateTime(subscription.cancelAt || subscription.currentPeriodEndAt)
                : `${subscription.invoiceCount} invoices, ${formatCurrency(subscription.totalCents)}`}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Payment Method</div>
            <div className="font-medium">{subscription.paymentMethodSummary || 'Family default'}</div>
            <Badge variant="outline" className="mt-1">
              {subscription.usesDefaultPaymentMethod ? 'Uses default' : 'Override'}
            </Badge>
          </div>
        </div>

        {cancelImmediatelyDisabledReason && (
          <div className="text-xs text-muted-foreground">
            Cancel immediately unavailable: {cancelImmediatelyDisabledReason}
          </div>
        )}

        {subscription.locallyManaged && (
          <SubscriptionDriftSection
            subscription={subscription}
            onActionCompleted={onActionCompleted}
          />
        )}

        {isCanceled ? (
          subscription.invoices.length > 0 && (
            <p className="border-t pt-4 text-sm text-muted-foreground">
              Its {subscription.invoices.length} invoice{subscription.invoices.length === 1 ? '' : 's'} now
              {subscription.converted ? ' live as invoices' : ' appear'} in the Invoices list below.
            </p>
          )
        ) : (
          subscription.invoices.length > 0 && (
            <div className="border-t pt-4">
              <InvoiceRowsTable invoices={subscription.invoices} onActionCompleted={onActionCompleted} />
            </div>
          )
        )}
      </CardContent>

      <AlertDialog open={showConvert} onOpenChange={setShowConvert}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Convert subscription to invoices?</AlertDialogTitle>
            <AlertDialogDescription>
              Each remaining installment becomes its own Stripe invoice that charges the family's
              saved payment method on its due date, and the subscription is cancelled straight away
              with no proration.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {convertReport && (
            <div className="space-y-3 text-sm">
              <div className="max-h-72 overflow-y-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Due date</TableHead>
                      <TableHead>Amount</TableHead>
                      {hasCardFees(convertReport.invoices) && (
                        <>
                          <TableHead>Card fee</TableHead>
                          <TableHead>Total</TableHead>
                        </>
                      )}
                      <TableHead>Memo</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {convertReport.invoices.map((row) => (
                      <TableRow key={row.invoiceId}>
                        <TableCell>{formatDate(row.dueDate)}</TableCell>
                        <TableCell>{formatCurrency(row.amountCents)}</TableCell>
                        {hasCardFees(convertReport.invoices) && (
                          <>
                            <TableCell>{row.feeCents > 0 ? formatCurrency(row.feeCents) : '-'}</TableCell>
                            <TableCell className="font-medium">{formatCurrency(row.amountCents + row.feeCents)}</TableCell>
                          </>
                        )}
                        <TableCell>{row.memo || '-'}</TableCell>
                        <TableCell>{convertRowStatus(row)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {hasCardFees(convertReport.invoices) && (
                <div className="text-muted-foreground">
                  The family's auto-pay method is a card, so each installment carries a card processing
                  fee line; the total is what the card will be charged.
                </div>
              )}

              {/* Ruling 22 (amended): one ending for every conversion, and Stripe
                  cannot resurrect a cancelled subscription, so say so here rather
                  than letting an admin discover it during an incident. */}
              <div className="font-medium">
                This cannot be undone: the subscription is cancelled in Stripe and cannot be restored.
                The month already paid stays paid; invoices start{' '}
                {formatDate(convertReport.invoices[0]?.dueDate ?? convertReport.periodEndOn)}.
              </div>

              {convertReport.reconciledCount > 0 && (
                <div className="text-muted-foreground">
                  Reconciled {convertReport.reconciledCount} already-billed month(s) from Stripe.
                </div>
              )}

              <ConvertWarnings warnings={convertReport.warnings} />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleConvert();
              }}
              disabled={convertLoading || !convertReport}
            >
              {convertLoading ? 'Converting...' : 'Convert'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCancelAtPeriodEnd} onOpenChange={setShowCancelAtPeriodEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel at period end?</AlertDialogTitle>
            <AlertDialogDescription>
              The subscription remains active until the current Stripe period ends, then stops future invoices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runAction(
                cancelAtPeriodEnd,
                { subscriptionId: subscription.subscriptionId },
                'cancelSubscriptionAtPeriodEnd',
                () => setShowCancelAtPeriodEnd(false)
              )}
              disabled={cancelAtPeriodEndLoading}
            >
              {cancelAtPeriodEndLoading ? 'Scheduling...' : 'Schedule Cancellation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCancelImmediately} onOpenChange={setShowCancelImmediately}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel immediately?</AlertDialogTitle>
            <AlertDialogDescription>
              This stops the Stripe subscription now and voids remaining locally scheduled subscription invoices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runAction(
                cancelImmediately,
                { subscriptionId: subscription.subscriptionId },
                'cancelSubscriptionImmediately',
                () => setShowCancelImmediately(false)
              )}
              disabled={cancelImmediatelyLoading}
              className="bg-destructive text-destructive-foreground"
            >
              {cancelImmediatelyLoading ? 'Canceling...' : 'Cancel Now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showResume} onOpenChange={setShowResume}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the scheduled cancellation or resumes a paused Stripe subscription.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runAction(
                resumeSubscription,
                { subscriptionId: subscription.subscriptionId },
                'resumeSubscription',
                () => setShowResume(false)
              )}
              disabled={resumeLoading}
            >
              {resumeLoading ? 'Resuming...' : 'Resume'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showChangeAmount} onOpenChange={setShowChangeAmount}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Next Amount</DialogTitle>
            <DialogDescription>
              This applies a one-time Stripe invoice item adjustment to the next scheduled subscription invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`next-amount-${subscription.subscriptionId}`}>Next invoice amount</Label>
            <Input
              id={`next-amount-${subscription.subscriptionId}`}
              type="number"
              step="0.01"
              min="0.01"
              value={nextAmount}
              onChange={(event) => setNextAmount(event.target.value)}
            />
            {nextScheduledInvoice && (
              <div className="text-xs text-muted-foreground">
                Current next invoice: {nextScheduledInvoice.memo || titleize(nextScheduledInvoice.key)} due {formatDate(nextScheduledInvoice.dueDate)}
              </div>
            )}
            {actionError && (
              <div className="text-sm text-destructive">
                {actionError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeAmount(false)}>Cancel</Button>
            <Button onClick={handleChangeAmount} disabled={changeAmountLoading}>
              {changeAmountLoading ? 'Saving...' : 'Save Amount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMoveBillingDate} onOpenChange={setShowMoveBillingDate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Billing Date</DialogTitle>
            <DialogDescription>
              This updates the trial end in Stripe and reschedules linked local tuition invoices.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`billing-date-${subscription.subscriptionId}`}>First billing date</Label>
            <Input
              id={`billing-date-${subscription.subscriptionId}`}
              type="date"
              value={billingDate}
              onChange={(event) => setBillingDate(event.target.value)}
            />
            {actionError && (
              <div className="text-sm text-destructive">
                {actionError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveBillingDate(false)}>Cancel</Button>
            <Button onClick={handleMoveBillingDate} disabled={moveBillingDateLoading}>
              {moveBillingDateLoading ? 'Moving...' : 'Move Date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface PostFee {
  invoiceId: string;
  label: string;
  feeCents: number;
  totalCents: number;
}

interface PostFailure {
  invoiceId: string;
  label: string;
  message: string;
}

function InvoiceTable({
  invoices,
  familyEnrollmentId,
  title = 'Invoices',
  onActionCompleted,
  onNewInvoice,
  onNewInvoiceSet,
  onSyncWithStripe,
  syncing = false,
  children,
  emptyMessage = 'No invoices found for this family and school year.',
  autoPayUnavailable = null,
}: {
  invoices: readonly FamilyBillingInvoice[];
  familyEnrollmentId?: string | null;
  autoPayUnavailable?: string | null;
  title?: string;
  onActionCompleted: () => void;
  onNewInvoice?: () => void;
  onNewInvoiceSet?: () => void;
  onSyncWithStripe?: () => void;
  syncing?: boolean;
  children?: React.ReactNode;
  emptyMessage?: string;
}) {
  // An invoice set is metadata on the rows, nothing more. The admin selects
  // rows and posts them; which rows may be selected is the backend's call
  // (`postable`), so the selection is read back through the rows every render
  // and a row that stops being postable — because it was just posted — drops
  // out of it on its own.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [collectionMethod, setCollectionMethod] = useState('send_invoice');
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  const [bulkWarnings, setBulkWarnings] = useState<readonly string[]>([]);
  const [postFailures, setPostFailures] = useState<readonly PostFailure[]>([]);
  // Ruling 30: an automatic row posted for a card family carries a fee line, so
  // the outcome says what each invoice will actually charge.
  const [postFees, setPostFees] = useState<readonly PostFee[]>([]);

  const [postInvoices, { loading: posting }] = usePostInvoices();

  const selectedInvoices = invoices.filter(
    (invoice) => invoice.postable && selectedIds.has(invoice.invoiceId)
  );
  const selectedCount = selectedInvoices.length;

  const toggleSelected = (invoiceId: string, selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (selected) next.add(invoiceId);
      else next.delete(invoiceId);
      return next;
    });
  };

  const openPostDialog = () => {
    setBulkError(null);
    setBulkNotice(null);
    setBulkWarnings([]);
    setPostFailures([]);
    setCollectionMethod('send_invoice');
    setShowPostDialog(true);
  };

  const handlePost = async () => {
    if (selectedCount === 0) return;

    setBulkError(null);
    setBulkNotice(null);
    setBulkWarnings([]);
    setPostFailures([]);
    setPostFees([]);

    if (!familyEnrollmentId) {
      setBulkError('Family enrollment is required.');
      return;
    }

    const invoiceIds = selectedInvoices.map((invoice) => invoice.invoiceId);
    const amounts = new Map(selectedInvoices.map((invoice) => [invoice.invoiceId, invoice.amountCents]));
    const result = await postInvoices({
      variables: { input: { familyEnrollmentId, invoiceIds, collectionMethod } },
    });

    const payload = result.data?.postInvoices;
    const message = mutationError(payload);
    const report = payload?.report;
    // The outcome belongs on the card, not behind the dialog that raised it, so
    // the dialog closes either way.
    setShowPostDialog(false);
    setBulkWarnings(report?.warnings ?? []);
    const labels = new Map(invoices.map((invoice) => [invoice.invoiceId, invoiceLabel(invoice)]));
    setPostFees(
      (report?.rows ?? [])
        .filter((row) => row.action === 'pushed' && row.feeCents > 0)
        .map((row) => ({
          invoiceId: row.invoiceId,
          label: labels.get(row.invoiceId) ?? row.invoiceId,
          feeCents: row.feeCents,
          totalCents: (amounts.get(row.invoiceId) ?? 0) + row.feeCents,
        }))
    );

    if (message || !payload?.success) {
      // The rows that failed stay selected so the retry is the same click again;
      // the ones Stripe took stop being postable once the card refetches.
      setPostFailures(
        (report?.rows ?? [])
          .filter((row) => row.action === 'failed')
          .map((row) => ({
            invoiceId: row.invoiceId,
            label: labels.get(row.invoiceId) ?? row.invoiceId,
            message: row.message || 'Stripe did not accept it.',
          }))
      );
      setBulkError(
        message ||
          (report
            ? `Stripe took ${report.pushedCount} of ${pluralInvoices(invoiceIds.length)}.`
            : 'Stripe did not accept the invoices.')
      );
      onActionCompleted();
      return;
    }

    setBulkNotice(`Posted ${pluralInvoices(report?.pushedCount ?? invoiceIds.length)} to Stripe. Queued ${report?.noticesQueued ?? 0} recipient notice(s).`);
    setSelectedIds(new Set());
    onActionCompleted();
  };

  const menu = onNewInvoice || onNewInvoiceSet || onSyncWithStripe ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Invoices actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onNewInvoice && (
          <DropdownMenuItem onClick={onNewInvoice}>
            <ReceiptText className="h-4 w-4" />
            New invoice...
          </DropdownMenuItem>
        )}
        {onNewInvoiceSet && (
          <DropdownMenuItem onClick={onNewInvoiceSet}>
            <Layers className="h-4 w-4" />
            New invoices...
          </DropdownMenuItem>
        )}
        {onSyncWithStripe && (
          <>
            <DropdownMenuSeparator />
            {/* Named for its object: the Payment Method card has its own
                "Sync from Stripe" and the two must not be mistaken. */}
            <DropdownMenuItem disabled={syncing} onClick={onSyncWithStripe}>
              <RefreshCw className="h-4 w-4" />
              {syncing ? 'Syncing invoices...' : 'Sync invoices with Stripe'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4" />
            {title}
          </span>
          <span className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={selectedCount === 0 || posting || !familyEnrollmentId}
              onClick={openPostDialog}
            >
              <Send className="h-4 w-4" />
              {selectedCount > 0 ? `Post ${pluralInvoices(selectedCount)}` : 'Post invoices'}
            </Button>
            {menu}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {children}

        {bulkNotice && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            {bulkNotice}
          </div>
        )}

        {postFees.length > 0 && (
          <div className="rounded-md border px-3 py-2 text-sm">
            <div>The family's auto-pay method is a card, so a card processing fee line was added:</div>
            <ul className="mt-1 list-disc pl-5">
              {postFees.map((fee) => (
                <li key={fee.invoiceId}>
                  {`${fee.label}: ${formatCurrency(fee.feeCents)} fee, charges ${formatCurrency(fee.totalCents)}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {bulkError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            <div>{bulkError}</div>
            {postFailures.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {postFailures.map((failure) => (
                  <li key={failure.invoiceId}>{`${failure.label}: ${failure.message}`}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {bulkWarnings.length > 0 && (
          <div className="space-y-1 text-sm text-amber-700">
            {bulkWarnings.map((warning) => (
              <div key={warning} className="flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                <span>{warning}</span>
              </div>
            ))}
          </div>
        )}

        {invoices.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <InvoiceRowsTable
            invoices={invoices}
            onActionCompleted={onActionCompleted}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
          />
        )}
      </CardContent>

      {/* Nothing about a DRAFT records how it will be collected — it has never
          reached Stripe — so the post has to ask. Manual by default (Decision 3);
          PostInvoices still refuses charge_automatically without consent. */}
      <Dialog open={showPostDialog} onOpenChange={(open) => { if (!open) setShowPostDialog(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post {pluralInvoices(selectedCount)} to Stripe</DialogTitle>
            <DialogDescription>
              Creates these invoices in Stripe. Families pay manual invoices from My Invoices in
              their SolaStack account; automatic ones charge on their due date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ul className="space-y-1 text-sm">
              {selectedInvoices.map((invoice) => (
                <li key={invoice.invoiceId} className="flex items-center justify-between gap-4">
                  <span>
                    {invoiceLabel(invoice)}
                    <span className="ml-2 text-xs text-muted-foreground">due {formatDate(invoice.dueDate)}</span>
                  </span>
                  <span className="font-medium">{formatCurrency(invoice.amountCents)}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-2">
              <Label htmlFor="post-invoices-collection">Collection</Label>
              <Select value={collectionMethod} onValueChange={setCollectionMethod}>
                <SelectTrigger id="post-invoices-collection">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="send_invoice">Manual payment in SolaStack</SelectItem>
                  <SelectItem value="charge_automatically" disabled={Boolean(autoPayUnavailable)}>
                    Charge saved payment method
                  </SelectItem>
                </SelectContent>
              </Select>
              {autoPayUnavailable && (
                <p className="text-sm text-muted-foreground">{autoPayUnavailable}</p>
              )}
              {collectionMethod === 'send_invoice' && (
                <p className="text-sm text-muted-foreground">
                  If invoice emails are enabled for this school, posting queues notices to guardian
                  email addresses. Check each invoice's notice status in Billing after posting.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostDialog(false)} disabled={posting}>
              Cancel
            </Button>
            <Button onClick={() => { void handlePost(); }} disabled={posting || selectedCount === 0}>
              <Send className="h-4 w-4" />
              {posting ? 'Posting...' : 'Post to Stripe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function BillingTab({
  schoolId,
  familySlug,
  schoolYearId,
  selectedYear,
  isActive,
  students = [],
  checklistEnrollmentOn = false,
  familyYear = null,
}: BillingTabProps) {
  const { data, loading, error, refetch } = useFamilyBillingQuery(
    schoolId,
    familySlug,
    schoolYearId,
    isActive
  );
  // Ruling 28: there is no separate one-off invoice dialog. "New invoice" is the
  // same wizard with one installment and a due date; "New invoices" is the
  // recurring form of it.
  const [showInvoiceSetWizard, setShowInvoiceSetWizard] = useState(false);
  const [wizardMode, setWizardMode] = useState<InvoiceSetWizardMode>('set');
  const [invoiceNotice, setInvoiceNotice] = useState<string | null>(null);
  const [syncReport, setSyncReport] = useState<SyncFamilyBillingReport | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [showStripeHistory, setShowStripeHistory] = useState(false);
  const [syncFamilyBilling, { loading: syncingInvoices }] = useSyncFamilyBilling();
  const [paymentMethodLinkError, setPaymentMethodLinkError] = useState<string | null>(null);
  const [paymentMethodLinkUrl, setPaymentMethodLinkUrl] = useState<string | null>(null);
  const [paymentMethodLinkCopied, setPaymentMethodLinkCopied] = useState(false);
  const [createPaymentMethodUpdateLink, { loading: paymentMethodLinkLoading }] = useCreatePaymentMethodUpdateLink();
  const [syncPaymentMethodError, setSyncPaymentMethodError] = useState<string | null>(null);
  const [syncPaymentMethodFromStripe, { loading: syncPaymentMethodLoading }] = useSyncPaymentMethodFromStripe();
  const [showLivePms, setShowLivePms] = useState(false);
  const familyEnrollmentId = data?.familyBilling?.familyEnrollmentId ?? undefined;
  const {
    data: livePmData,
    loading: livePmLoading,
    error: livePmError,
    refetch: refetchLivePms,
  } = useFamilyStripePaymentMethodsQuery(familyEnrollmentId, { skip: !showLivePms });
  const [setDefaultPaymentMethod, { loading: setDefaultLoading }] = useSetDefaultPaymentMethod();
  const [settingDefaultPmId, setSettingDefaultPmId] = useState<string | null>(null);

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!familyEnrollmentId) return;
    setSettingDefaultPmId(paymentMethodId);
    try {
      await setDefaultPaymentMethod({ variables: { familyEnrollmentId, paymentMethodId } });
      await Promise.all([refetchLivePms(), refetch()]);
    } finally {
      setSettingDefaultPmId(null);
    }
  };

  const [setSubscriptionPaymentMethod, { loading: settingSubPm }] = useSetSubscriptionPaymentMethod();
  const hasSubscription = (data?.familyBilling?.subscriptions ?? []).some((s) => s.stripeSubscriptionId);

  const handleSetSubscriptionPaymentMethod = async (paymentMethodId: string | null) => {
    if (!familyEnrollmentId) return;
    await setSubscriptionPaymentMethod({ variables: { familyEnrollmentId, paymentMethodId } });
    await refetch();
  };

  if (!selectedYear || !schoolYearId) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Select a school year to view billing.
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading billing...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        Error loading billing: {error.message}
      </div>
    );
  }

  const billing = data?.familyBilling;

  if (!billing) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Billing data was not found for this family and school year.
        </CardContent>
      </Card>
    );
  }

  // Ruling 26: read-only against Stripe. It settles what Stripe already
  // collected, points local rows at Stripe invoices they were missing, and lists
  // what Stripe has that SolaStack does not. It never voids anything.
  const handleSyncInvoicesWithStripe = async () => {
    setSyncError(null);

    if (!billing.familyEnrollmentId) {
      setSyncError('Family enrollment is required.');
      return;
    }

    const result = await syncFamilyBilling({
      variables: { input: { familyEnrollmentId: billing.familyEnrollmentId } },
    });

    const payload = result.data?.syncFamilyBilling;
    const message = mutationError(payload);
    if (message || !payload?.success) {
      setSyncError(message || 'The Stripe sync did not finish.');
      setSyncReport(null);
      return;
    }

    setSyncReport(payload.report as SyncFamilyBillingReport);
    await refetch();
  };

  const openWizard = (mode: InvoiceSetWizardMode) => {
    setInvoiceNotice(null);
    setWizardMode(mode);
    setShowInvoiceSetWizard(true);
  };

  const handlePaymentMethodUpdate = async () => {
    setPaymentMethodLinkError(null);
    setPaymentMethodLinkCopied(false);

    if (!billing.familyEnrollmentId) {
      setPaymentMethodLinkError('Family enrollment is required.');
      return;
    }

    const result = await createPaymentMethodUpdateLink({
      variables: {
        input: {
          familyEnrollmentId: billing.familyEnrollmentId,
          returnUrl: window.location.href,
        },
      },
    });

    const message = mutationError(result.data?.createPaymentMethodUpdateLink);
    const url = result.data?.createPaymentMethodUpdateLink?.url;
    if (message || !url) {
      setPaymentMethodLinkError(message || 'Stripe did not return a portal link.');
      return;
    }

    setPaymentMethodLinkUrl(url);
  };

  const handleSyncPaymentMethodFromStripe = async () => {
    setSyncPaymentMethodError(null);

    if (!billing.familyEnrollmentId) {
      setSyncPaymentMethodError('Family enrollment is required.');
      return;
    }

    const result = await syncPaymentMethodFromStripe({
      variables: {
        input: {
          familyEnrollmentId: billing.familyEnrollmentId,
        },
      },
    });

    const message = mutationError(result.data?.syncPaymentMethodFromStripe);
    if (message) {
      setSyncPaymentMethodError(message);
      return;
    }

    await refetch();
  };

  const handleCopyPaymentMethodLink = async () => {
    if (!paymentMethodLinkUrl) return;
    try {
      await navigator.clipboard.writeText(paymentMethodLinkUrl);
      setPaymentMethodLinkCopied(true);
      window.setTimeout(() => setPaymentMethodLinkCopied(false), 2000);
    } catch {
      setPaymentMethodLinkError('Could not copy — select the link and copy manually.');
    }
  };

  const closePaymentMethodLinkDialog = () => {
    setPaymentMethodLinkUrl(null);
    setPaymentMethodLinkCopied(false);
  };

  // Only a live subscription owns its invoices. Once it is cancelled (converted
  // or otherwise) the rows are just invoices — Stripe-backed or paid — and belong
  // in the Invoices list with everything else; the card keeps only its history
  // line. The snapshot still attaches them to the subscription for that history.
  const ownsInvoices = (subscription: (typeof billing.subscriptions)[number]) =>
    subscription.status !== 'subscription_canceled';
  const groupedInvoiceIds = new Set(
    billing.subscriptions
      .filter(ownsInvoices)
      .flatMap((subscription) => subscription.invoices.map((invoice) => invoice.invoiceId))
  );
  // Findings 1 and 5: every invoice is a row on the one Invoices card, in
  // due-date order. A scheduleKey is metadata on the rows and nothing on the
  // page groups or acts on it. A live subscription's invoices are the exception:
  // its card above already lists them.
  const cardInvoices = billing.invoices
    .filter((invoice) => !groupedInvoiceIds.has(invoice.invoiceId))
    .slice()
    .sort((a, b) => dueTime(a.dueDate) - dueTime(b.dueDate));
  const autoPay = autoPayStatus(billing);

  return (
    <div className="space-y-6">
      <WarningList warnings={billing.warnings} />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            {/* Three buttons and a heading do not fit across half a grid column,
                so the row wraps rather than running the last one under the card
                edge. min-w-0 lets the heading give up its width first. */}
            <CardTitle className="text-base flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <span className="flex min-w-0 items-center gap-2">
                <CreditCard className="h-4 w-4 shrink-0" />
                Payment methods
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!billing.familyEnrollmentId || paymentMethodLinkLoading}
                  onClick={() => { void handlePaymentMethodUpdate(); }}
                  className="whitespace-nowrap"
                >
                  {paymentMethodLinkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
                  Send add-method link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!billing.familyEnrollmentId || syncPaymentMethodLoading}
                  onClick={() => { void handleSyncPaymentMethodFromStripe(); }}
                  className="whitespace-nowrap"
                >
                  {syncPaymentMethodLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Sync from Stripe
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!billing.familyEnrollmentId}
                  onClick={() => openWizard('single')}
                  className="whitespace-nowrap"
                >
                  <Plus className="h-4 w-4" />
                  Create invoice
                </Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {paymentMethodLinkError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
                {paymentMethodLinkError}
              </div>
            )}
            {syncPaymentMethodError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
                {syncPaymentMethodError}
              </div>
            )}
            {/* Two concepts, named plainly (ruling, 2026-09-12): the auto-pay
                method automatic invoices charge, and the saved methods a parent
                chooses from when paying an invoice themselves. */}
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Auto-pay method</span>
              <span className="font-medium text-right">{billing.paymentMethod.summary}</span>
            </div>
            <p className="text-muted-foreground">
              Automatic invoices are charged to the auto-pay method. When the family pays an invoice
              themselves, they choose which saved method to use.
            </p>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Autopay</span>
              <Badge variant={autoPay.on ? 'default' : 'outline'}>{autoPay.word}</Badge>
            </div>
            {autoPay.note && <p className="text-right text-muted-foreground">{autoPay.note}</p>}
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Stripe Customer</span>
              <StripeLink href={billing.paymentMethod.stripeDashboardUrl} />
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Methods on file (live from Stripe)</span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!billing.familyEnrollmentId || livePmLoading}
                  onClick={() => setShowLivePms(true)}
                >
                  {livePmLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {showLivePms ? 'Refresh' : 'Show'}
                </Button>
              </div>
              {livePmError && (
                <div className="mt-2 text-destructive">Could not read payment methods from Stripe.</div>
              )}
              {showLivePms && !livePmLoading && (
                <ul className="mt-2 space-y-1">
                  {(livePmData?.familyStripePaymentMethods ?? []).length === 0 && (
                    <li className="text-muted-foreground">No payment methods on file in Stripe.</li>
                  )}
                  {(livePmData?.familyStripePaymentMethods ?? []).map((pm) => (
                    <li key={pm.paymentMethodId} className="flex items-center justify-between gap-3">
                      <span>
                        {pm.paymentType === 'card' ? (pm.brand ? `${pm.brand} card` : 'Card')
                          : pm.paymentType === 'us_bank_account' ? 'Bank account'
                          : pm.paymentType === 'link' ? (pm.linkEmail ? `Link (${pm.linkEmail})` : 'Link')
                          : pm.paymentType}
                        {pm.last4 ? ` ····${pm.last4}` : ''}
                      </span>
                      {pm.isDefault ? (
                        <Badge variant="default">Auto-pay</Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={setDefaultLoading}
                          onClick={() => { void handleSetDefaultPaymentMethod(pm.paymentMethodId); }}
                        >
                          {settingDefaultPmId === pm.paymentMethodId
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : 'Use for auto-pay'}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {showLivePms && !livePmLoading && hasSubscription && (
                <div className="mt-3 border-t pt-3">
                  <div className="text-muted-foreground mb-1">Subscription charges</div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={settingSubPm}
                      onClick={() => { void handleSetSubscriptionPaymentMethod(null); }}
                    >
                      Use auto-pay method
                    </Button>
                    {(livePmData?.familyStripePaymentMethods ?? []).map((pm) => (
                      <Button
                        key={`sub-${pm.paymentMethodId}`}
                        variant="outline"
                        size="sm"
                        disabled={settingSubPm}
                        onClick={() => { void handleSetSubscriptionPaymentMethod(pm.paymentMethodId); }}
                      >
                        Use {pm.paymentType === 'card' ? (pm.brand ?? 'card')
                          : pm.paymentType === 'us_bank_account' ? 'bank'
                          : pm.paymentType}{pm.last4 ? ` ····${pm.last4}` : ''}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Billing Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">School Year</span>
              <span className="font-medium text-right">{billing.schoolYearName}</span>
            </div>
            {/* With checklist enrollment on, every year reads its access, as on
                the office's other screens. The school's own Stripe account is on
                Getting paid, not here. */}
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Enrollment</span>
              <span className="font-medium text-right">
                {familyYear && isNewStyle(familyYear, checklistEnrollmentOn)
                  ? ACCESS_LABELS[familyYear.access] ?? familyYear.access
                  : titleize(billing.enrollmentState)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {billing.subscriptions.length > 0 && (
        <div className="space-y-4">
          {billing.subscriptions.map((subscription) => (
            <SubscriptionCard
              key={subscription.subscriptionId}
              subscription={subscription}
              onActionCompleted={() => { void refetch(); }}
            />
          ))}
        </div>
      )}

      {/* Ruling 24: one card, always called "Invoices". A title that changed with
          the page's contents made the same table read as two different things. */}
      <InvoiceTable
        invoices={cardInvoices}
        familyEnrollmentId={billing.familyEnrollmentId}
        autoPayUnavailable={autoPayUnavailableReason(billing)}
        emptyMessage="No other invoices."
        onActionCompleted={() => { void refetch(); }}
        onNewInvoice={() => openWizard('single')}
        onNewInvoiceSet={() => openWizard('set')}
        onSyncWithStripe={() => { void handleSyncInvoicesWithStripe(); }}
        syncing={syncingInvoices}
      >
        {invoiceNotice && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            {invoiceNotice}
          </div>
        )}

        {syncError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {syncError}
          </div>
        )}

        {syncReport && (
          <div className="space-y-2 text-sm">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
              Stripe sync: {syncReport.settledCount} settled, {syncReport.stampedCount} linked,{' '}
              {syncReport.history.filter((row) => !row.otherSchoolYearName).length} Stripe invoice(s)
              with no SolaStack row.
            </div>

            {syncReport.warnings.length > 0 && (
              <div className="space-y-1 text-amber-700">
                {syncReport.warnings.map((warning) => (
                  <div key={warning} className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Ruling 26: history is what Stripe charged before SolaStack
                mirrored anything. Read-only, and never turned into local rows. */}
            {syncReport.history.length > 0 && (
              <div className="rounded-md border">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between"
                  onClick={() => setShowStripeHistory((current) => !current)}
                >
                  <span>Stripe history ({syncReport.history.length})</span>
                  {showStripeHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
                {showStripeHistory && (
                  <div className="overflow-x-auto border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead className="text-right">Paid</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>In SolaStack</TableHead>
                          <TableHead className="w-12">
                            <span className="sr-only">Stripe</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncReport.history.map((row) => (
                          <TableRow key={row.stripeInvoiceId}>
                            <TableCell>
                              <div className="font-medium">{row.number || row.stripeInvoiceId}</div>
                              {row.description && (
                                <div className="text-xs text-muted-foreground">{row.description}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(row.periodStart)} – {formatDate(row.periodEnd)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(row.amountPaid)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{titleize(row.status)}</Badge>
                            </TableCell>
                            {/* The Stripe customer is family-wide, so this list
                                spans every year. A row another year already owns
                                is not a gap in this year's mirror. */}
                            <TableCell className="text-sm text-muted-foreground">
                              {row.otherSchoolYearName
                                ? `${row.otherSchoolYearName} — see that year`
                                : 'No SolaStack row'}
                            </TableCell>
                            <TableCell>
                              <StripeLink href={row.hostedUrl} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </InvoiceTable>

      {/* Keyed by mode: the wizard's defaults — one installment or ten, a due
          date or a first due date — are initial state, so switching between
          "New invoice" and "New invoices" has to give a fresh dialog. */}
      <InvoiceSetWizard
        key={wizardMode}
        open={showInvoiceSetWizard}
        onOpenChange={setShowInvoiceSetWizard}
        familyEnrollmentId={billing.familyEnrollmentId}
        students={students}
        mode={wizardMode}
        autoPayUnavailableReason={autoPayUnavailableReason(billing)}
        onCompleted={(notice) => {
          setInvoiceNotice(notice ?? null);
          void refetch();
        }}
      />

      <Dialog
        open={!!paymentMethodLinkUrl}
        onOpenChange={(open) => { if (!open) closePaymentMethodLinkDialog(); }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment method update link</DialogTitle>
            <DialogDescription>
              Share this link with the parent. It opens the Stripe portal where they can add or change their payment method.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs break-all font-mono">
              {paymentMethodLinkUrl}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { void handleCopyPaymentMethodLink(); }}>
                <Copy className="h-4 w-4" />
                {paymentMethodLinkCopied ? 'Copied!' : 'Copy link'}
              </Button>
              {paymentMethodLinkUrl && (
                <Button variant="outline" asChild>
                  <a href={paymentMethodLinkUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    Open in new tab
                  </a>
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Stripe portal links can expire — generate a fresh one if the parent gets an "expired" message.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closePaymentMethodLinkDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BillingTab;
